import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { Label } from "@/components/ui/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import {
  SheetBody,
  SheetClose,
  SheetFooter,
} from "@/components/ui/Sheet/Sheet";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import { Lock, OctagonX } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { useSignUp } from "../../hooks/dashboard/useSignUp";
import { api } from "@/utils/api";
import { useAuth } from "@/context/authContext";
import { MultiSessionConfirmDialog } from "@/artifacts/student/components/dashboard/OpenSignups/MultiSessionConfirmDialog";

const schema = z.object({
  choices: z
    .array(z.string("Choices must be strings"))
    .length(3, "3 choices are required")
    .superRefine((choices, ctx) => {
      const seen = new Map<string, number>();
      choices.forEach((v, i) => {
        if (!v) return;
        const prev = seen.get(v);
        if (prev !== undefined) {
          ctx.addIssue({
            code: "custom",
            path: [i],
            message: `Already selected in Preference ${prev + 1}`,
          });
        } else {
          seen.set(v, i);
        }
      });
    }),
});

export type MultiWeekSignUpFormFields = z.infer<typeof schema>;

const MultiWeekSignUpForm = ({
  data,
  loading,
  onSuccess,
}: {
  data: {
    explorations: any[];
    sessionId: string;
  };
  loading: boolean;
  onSuccess: (sessionId: string) => void;
}) => {
  const { dispatch } = useAuth();
  const { preferences, loadPreferenceContext } = useSignUp();

  const [lockedChoice1, setLockedChoice1] = useState<{
    explorationId: string;
    sourceSessionName: string;
  } | null>(null);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MultiWeekSignUpFormFields>({
    resolver: zodResolver(schema),
    defaultValues: { choices: ["", "", ""] },
  });

  useEffect(() => {
    if (!data.sessionId) return;
    (async () => {
      const ctx = await loadPreferenceContext(data.sessionId);
      if (ctx?.lockedChoice1) {
        setLockedChoice1({
          explorationId: ctx.lockedChoice1.explorationId,
          sourceSessionName: ctx.lockedChoice1.sourceSession?.name ?? "",
        });
        setValue("choices.0", ctx.lockedChoice1.explorationId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.sessionId]);

  const choices = watch("choices");

  const [confirm, setConfirm] = useState<{
    open: boolean;
    pending?: MultiWeekSignUpFormFields;
    exploration: any;
    siblings: any[];
    sessions: any[];
    rotations: any[];
  }>({
    open: false,
    pending: undefined,
    exploration: null,
    siblings: [],
    sessions: [],
    rotations: [],
  });

  const submitPreferences = async (formData: MultiWeekSignUpFormFields) => {
    const result = await preferences(data.sessionId, formData.choices);
    if (result?.session) onSuccess(result.session);
  };

  const onSubmit: SubmitHandler<MultiWeekSignUpFormFields> = async (
    formData,
  ) => {
    const choice1 = data.explorations.find(
      (e) => e._id === formData.choices[0],
    );
    const isMultiChoice1 = Boolean(choice1?.multiSessionGroup);
    const lockAlreadyApplied =
      lockedChoice1 && lockedChoice1.explorationId === formData.choices[0];

    if (isMultiChoice1 && !lockAlreadyApplied) {
      try {
        const response = await api(
          `/explorations/multi-session/${formData.choices[0]}`,
          { method: "GET" },
          dispatch,
        );
        setConfirm({
          open: true,
          pending: formData,
          exploration: choice1,
          siblings: response.siblings ?? [],
          sessions: response.sessions ?? [],
          rotations: response.rotations ?? [],
        });
        return;
      } catch {
        // Fall through to direct submission if siblings fetch fails
      }
    }

    await submitPreferences(formData);
  };

  return (
    <>
      <SheetBody>
        <form id="multi-week-sign-up-form" onSubmit={handleSubmit(onSubmit)}>
          <VStack>
            {Array.from({ length: 3 }).map((_, i) => (
              <VStack gap="2px" key={i}>
                <HStack gap="0.25rem" align="center">
                  <Label>Preference {i + 1}</Label>
                  {i === 0 && lockedChoice1 && (
                    <Lock
                      style={{
                        width: "0.875rem",
                        height: "0.875rem",
                        color: "var(--muted-foreground)",
                      }}
                    />
                  )}
                  {errors.choices?.[i] && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <OctagonX
                          style={{
                            color: "var(--danger)",
                            width: "1rem",
                            height: "1rem",
                            cursor: "default",
                          }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {errors.choices?.[i].message}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </HStack>
                {i === 0 && lockedChoice1 && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    Locked from your multi-session pick in
                    {" "}
                    {lockedChoice1.sourceSessionName || "another session"}.
                  </span>
                )}
                {loading ? (
                  <Skeleton style={{ height: "36px", width: "100%" }} />
                ) : (
                  <Controller
                    control={control}
                    name={`choices.${i}`}
                    render={({ field }) => {
                      const taken = new Set(
                        (choices ?? []).filter((v, idx) => v && idx !== i),
                      );
                      const disabled = i === 0 && !!lockedChoice1;

                      return (
                        <Select
                          value={field.value}
                          onValueChange={(v) => field.onChange(v)}
                          disabled={disabled}
                        >
                          <SelectTrigger aria-invalid={!!errors.choices?.[i]}>
                            <SelectValue placeholder="Select an exploration" />
                          </SelectTrigger>
                          <SelectContent>
                            {data.explorations.map((e) => (
                              <SelectItem
                                key={e._id}
                                value={e._id}
                                disabled={taken.has(e._id)}
                              >
                                {e.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />
                )}
              </VStack>
            ))}
          </VStack>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button
          type="submit"
          form="multi-week-sign-up-form"
          style={{ width: "100%" }}
        >
          Sign Up
        </Button>
        <SheetClose asChild>
          <Button variant="outline" style={{ width: "100%" }}>
            Cancel
          </Button>
        </SheetClose>
      </SheetFooter>
      <MultiSessionConfirmDialog
        open={confirm.open}
        onOpenChange={(v) => setConfirm((prev) => ({ ...prev, open: v }))}
        onConfirm={async () => {
          const pending = confirm.pending;
          setConfirm((prev) => ({ ...prev, open: false }));
          if (pending) await submitPreferences(pending);
        }}
        exploration={confirm.exploration ?? { name: "" }}
        siblings={confirm.siblings}
        sessions={confirm.sessions}
        rotations={confirm.rotations}
        variant="preference"
      />
    </>
  );
};

export { MultiWeekSignUpForm };
